import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AlgoliaSearchParams } from "../../../../../api/postCall/algoliaSearch";
import CustomTable from "../../../../../components/custom-table/custom-table";
import { useTablesContext } from "../../../../../context/tablesContextProvider";
import { FilterValues } from "../../../../../data-model/filterValues";
import { ModalId } from "../../../../../enums/modalId";
import { setFilter } from "../../../../../redux/slice/filterValuesSlice";
import {
  fetchDataUptoGivenPageNumberFromExternalTablePoweredByAlgolia,
  fetchExternalTablePoweredByAlgoliaData,
  fetchMoreExternalTablePoweredByAlgoliaData,
  resetExternalTablePoweredByAlgoliaData,
} from "../../../../../redux/slice/medicalPa/externalTablePoweredByAlgoliaSlice";
import { setOpenedModalId } from "../../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import {
  processFilterData,
  transformDateFields,
  transformObjectToKeyValueArray,
} from "../../../../../utils/queryConstraint";
import {
  FILTER_CREATION_CONFIG,
  useFilterOptionsGeneratorFromAlgolia,
} from "../../../hooks";
import {
  AllPaOrdersForExternalTableByAlgoliaHeaders,
  generateAllPaOrdersForExternalByAlgoliaTableData,
} from "../utils/allPaOrdersForExternalByAlgoliaHelper";
import { generatePayloadForInitialDataFetch } from "../utils/generatePayloadForInitialDataFetch";

const AllPaOrdersForExternalTableByAlgolia = ({
  allPaOrdersForExternalTableRef,
  exportingInProgress,
}: {
  allPaOrdersForExternalTableRef: React.RefObject<HTMLDivElement>;
  exportingInProgress: boolean;
}) => {
  const { data, loading, showinLineLoader } = useSelector(
    (state: RootState) => state.externalTablePoweredByAlgolia,
  );
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { filters } = useSelector((state: RootState) => state.filterValues);
  const [endIndex, setEndIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [paginationResetCount, setPaginationResetCount] = useState(0);
  const { filterData, algoliaMultiFacetsCountLoading } =
    useFilterOptionsGeneratorFromAlgolia(FILTER_CREATION_CONFIG);
  const [filterPayload, setFilterPayload] =
    useState<AlgoliaSearchParams | null>();
  const [isTableRefActive, setIsTableRefActive] = useState(false);
  const [initialQueryExecuted, setInitialQueryExecuted] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();

  const tableAttributes = getTableDataFromContext(
    "allMedicalPaOrdersForExternalByAlgolia",
  );

  const fetchDataWithoutFilterRecursively = () => {
    const payloads = generatePayloadForInitialDataFetch(
      {
        org_id: getOrgIdForFetchExternalWorklist(),
        query: "",
        facet_filters: [],
        hits_per_page: 400,
        page: tableAttributes?.lastBatchFetched ?? 0,
      },
      tableAttributes?.lastBatchFetched ?? 0,
      searchTerm ?? "",
    );

    dispatch(
      fetchDataUptoGivenPageNumberFromExternalTablePoweredByAlgolia(payloads),
    );
  };

  const fetchDataWithSearchTermRecursively = () => {
    const payloads = generatePayloadForInitialDataFetch(
      {
        org_id: getOrgIdForFetchExternalWorklist(),
        query: searchTerm,
        facet_filters: [],
        hits_per_page: 400,
        page: tableAttributes?.lastBatchFetched ?? 0,
      },
      tableAttributes?.lastBatchFetched ?? 0,
      searchTerm ?? "",
    );
    dispatch(
      fetchDataUptoGivenPageNumberFromExternalTablePoweredByAlgolia(payloads),
    );
  };

  const fetchDataWithoutFilter = () => {
    if (initialQueryExecuted) {
      dispatch(
        fetchExternalTablePoweredByAlgoliaData({
          org_id: getOrgIdForFetchExternalWorklist(),
          query: "",
          facet_filters: [],
          hits_per_page: 400,
          page: 0,
        }),
      );
    } else {
      fetchDataWithoutFilterRecursively();
    }
  };

  const fetchDataWithFilterRecursively = () => {
    const payloads = generatePayloadForInitialDataFetch(
      filterPayload as AlgoliaSearchParams,
      tableAttributes?.lastBatchFetched ?? 0,
      searchTerm ?? "",
    );
    dispatch(
      fetchDataUptoGivenPageNumberFromExternalTablePoweredByAlgolia(payloads),
    );
  };

  const fetchDataWithSearchTerm = (fromInitialDataFetch: boolean) => {
    if (initialQueryExecuted) {
      dispatch(
        fetchExternalTablePoweredByAlgoliaData({
          org_id: getOrgIdForFetchExternalWorklist(),
          query: searchTerm,
          facet_filters: [],
          hits_per_page: 400,
          page: fromInitialDataFetch
            ? 0
            : (tableAttributes?.lastBatchFetched ?? 0),
        }),
      );
    } else {
      fetchDataWithSearchTermRecursively();
    }
  };

  const fetchDataWithFilter = (fromInitialDataFetch: boolean) => {
    if (initialQueryExecuted) {
      dispatch(
        fetchExternalTablePoweredByAlgoliaData(
          filterPayload as AlgoliaSearchParams,
        ),
      );
    } else {
      fetchDataWithFilterRecursively();
    }
  };
  useEffect(() => {
    if (isTableRefActive) {
      if (validFilters && validFilters[0]?.["filterCount"] > 0) {
        fetchDataWithFilter(false);
      }
      if (searchTerm) {
        initialQueryExecuted
          ? fetchDataWithSearchTerm(true)
          : fetchDataWithSearchTerm(false);
      } else {
        fetchDataWithoutFilter();
      }
      initialQueryExecuted && setPaginationResetCount((prev) => prev + 1);
      setInitialQueryExecuted(true);
    }
  }, [filterPayload, isTableRefActive]);

  const handleFilterApply = (data: FilterValues) => {
    dispatch(setFilter(data));
  };

  const validFilters = useMemo(() => {
    return filters.filter(
      (filter) => filter.name === "allMedicalPaOrdersForExternalByAlgolia",
    );
  }, [filters]);

  const tableData = useMemo(() => {
    if (data && authStatusOptions) {
      return generateAllPaOrdersForExternalByAlgoliaTableData(
        data.hits ?? [],
        authStatusOptions,
      );
    }
    return [];
  }, [data?.page, authStatusOptions]);

  const handleEndIndexOfTable = (endIndex: number) => {
    setEndIndex(endIndex);
  };

  const exportDataForTable = () => {
    dispatch(
      setOpenedModalId({
        id: ModalId.ANALYTICS_DATE_PICKER_MODAL,
      }),
    );
  };

  const isFilterApplied = useMemo(() => {
    return (
      validFilters &&
      validFilters.length > 0 &&
      validFilters?.[0]?.["filterCount"] > 0
    );
  }, [validFilters]);

  const handleNavigateToNextPage = (rowData: any) => {
    navigate(`/external-worklist/singleOrderForExternal/${rowData.id}`);
  };

  const handleRefreshButtonClick = () => {
    if (isFilterApplied) {
      fetchDataWithFilterRecursively();
    } else if (searchTerm) {
      fetchDataWithSearchTermRecursively();
    } else {
      fetchDataWithoutFilterRecursively();
    }
  };

  useEffect(() => {
    if (
      endIndex === (data?.hits?.length ?? 0) - 1 &&
      (data?.page ?? 0) < (data?.nb_pages ?? 0) - 1
    ) {
      const query = filterPayload ?? "";
      const payload =
        query !== ""
          ? {
              ...filterPayload,
              page: (tableAttributes?.lastBatchFetched ?? 0) + 1,
            }
          : {
              org_id: getOrgIdForFetchExternalWorklist(),
              query: searchTerm,
              facet_filters: [],
              hits_per_page: 400,
              page: (tableAttributes?.lastBatchFetched ?? 0) + 1,
            };
      dispatch(
        fetchMoreExternalTablePoweredByAlgoliaData(
          payload as AlgoliaSearchParams,
        ),
      );
    }

    initialQueryExecuted &&
      setTableDataForContext("allMedicalPaOrdersForExternalByAlgolia", {
        lastBatchFetched: data?.page ?? 0,
      });
  }, [endIndex]);

  useEffect(() => {
    const filterData = processFilterData(validFilters);

    const createdFilterQuery = transformObjectToKeyValueArray(filterData).map(
      (item) => item,
    );

    const dateFilterQuery = transformDateFields(filterData);
    const payload = {
      org_id: getOrgIdForFetchExternalWorklist(),
      query: searchTerm,
      facet_filters: createdFilterQuery,
      hits_per_page: 400,
      page: 0,
      ...dateFilterQuery,
    };

    setFilterPayload(payload);
  }, [validFilters, searchTerm, isTableRefActive]);

  useEffect(() => {
    if (allPaOrdersForExternalTableRef.current) {
      setIsTableRefActive(true);
    }
  }, [allPaOrdersForExternalTableRef]);

  useEffect(() => {
    return () => {
      dispatch(resetExternalTablePoweredByAlgoliaData());
    };
  }, []);

  return (
    <div className="h-full" ref={allPaOrdersForExternalTableRef}>
      <CustomTable
        tableHeaders={AllPaOrdersForExternalTableByAlgoliaHeaders}
        tableData={tableData}
        tableName={"allMedicalPaOrdersForExternalByAlgolia"}
        itemsPerPage={20}
        pagesPerView={5}
        count={tableData.length ?? 0}
        totalCount={data?.nb_hits ?? 0}
        endIndexOfTable={handleEndIndexOfTable}
        searchingText={(searchText: string) => {
          setSearchTerm(searchText);
        }}
        hideSearchBar={false}
        onRowClick={(rowData: any) => {
          handleNavigateToNextPage(rowData);
        }}
        isFetching={loading}
        showExportButton={true}
        onExportButtonClick={exportDataForTable}
        showInLineLoader={showinLineLoader}
        showRefreshButton={true}
        onRefreshButton={handleRefreshButtonClick}
        isDisabled={loading}
        filterSectionsData={filterData}
        onFilterApplyClicked={handleFilterApply}
        requestForPaginationReset={paginationResetCount}
        exportingInProgress={exportingInProgress}
        showFilterButton={!loading && !algoliaMultiFacetsCountLoading}
      />
    </div>
  );
};

export default AllPaOrdersForExternalTableByAlgolia;
